import { type ColumnDef } from "@tanstack/react-table";
import type { ApiUser } from "../types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowUpDown,
  CircleEllipsis,
  PenBox,
  ToggleLeft,
  ToggleRight,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { NavLink } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import DeleteButton from "@/components/delete-button";
import { usersRepo } from "@/lib/repositories";
import { toast } from "sonner";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { Checkbox } from "@/components/ui/checkbox";

async function onArchive(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(usersRepo.patch(id, { status: "inactive" }));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["users"] });
  toast.success(t("messages.user_archive_success"));
}

async function onUnarchive(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(usersRepo.patch(id, { status: "active" }));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["users"] });
  toast.success(t("messages.user_unarchive_success"));
}

async function onDelete(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(usersRepo.delete(id));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["users"] });
  toast.success(t("messages.user_delete_success"));
}

export const usersColumns = (t: TFunction): ColumnDef<ApiUser>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t("keys.select_all")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t("keys.select_row")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "reference",
    header: t("keys.id_reference"),
    cell: ({ row }) => {
      return (
        <NavLink to={ROUTES.USER_DETAILS(row.original.id)} end>
          <span className="text-gray-400 hover:text-primary hover:underline">
            {row.getValue("reference")}
          </span>
        </NavLink>
      );
    },
  },
  {
    accessorKey: "last_name",
    header: t("keys.last_name"),
    cell: ({ row }) => {
      return (
        <span className="text-gray-500 truncate inline-block max-w-56">
          {row.getValue("last_name")}
        </span>
      );
    },
  },
  {
    accessorKey: "first_name",
    header: t("keys.first_name"),
    cell: ({ row }) => {
      return (
        <span className="text-gray-500 truncate inline-block max-w-56">
          {row.getValue("first_name")}
        </span>
      );
    },
  },
  {
    accessorKey: "email",
    header: t("keys.email"),
    cell: ({ row }) => {
      return (
        <span className="text-gray-500 truncate inline-block max-w-56">
          {row.getValue("email")}
        </span>
      );
    },
  },
  {
    accessorKey: "groups",
    header: t("keys.role"),
    cell: ({ row }) => {
      const groups = row.getValue("groups") as ApiUser["groups"];
      if (groups.length === 0) {
        return <span className="text-gray-500">-</span>;
      }
      return (
        <Badge variant="secondary">
          {groups.map((group) => t("keys.roles." + group.name)).join(", ")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.status")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("status") === "active";
      return (
        <Badge variant={isActive ? "success" : "destructive"}>
          {isActive ? t("keys.active") : t("keys.desactivated")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "date_joined",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.creation_date")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      return (
        <span className="text-gray-500">
          {formatDate(row.getValue("date_joined"))}
        </span>
      );
    },
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      const isActive = row.original.status === "active";
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <NavLink to={ROUTES.USER_DETAILS(row.original.id)} end>
                <PenBox />
                {t("keys.table.edit")}
              </NavLink>
            </DropdownMenuItem>
            {isActive ? (
              <DropdownMenuItem onClick={() => onArchive(row.original.id, t)}>
                <ToggleLeft />
                {t("keys.inactive_action")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onUnarchive(row.original.id, t)}>
                <ToggleRight />
                {t("keys.active_action")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <NavLink
                to={`${ROUTES.HISTORY}?object_pk=${row.original.id}&model_type=user`}
                end
              >
                <History />
                {t("keys.table.view_history")}
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteButton
                onClick={() => onDelete(row.original.id, t)}
                className="w-full justify-start"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
